package com.isaias.fyattravel.web.pages.flights

import androidx.compose.runtime.*
import kotlinx.browser.window
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.fetch.RequestInit
import org.w3c.fetch.Headers
import com.isaias.fyattravel.web.home.DynamicHeroSection
import com.isaias.fyattravel.web.flight.EnhancedFlightSearchCard
import com.isaias.fyattravel.web.flight.FlightSearchParams
import com.isaias.fyattravel.web.components.ServiceTabs
import com.isaias.fyattravel.web.components.BookingEnquiryForm
import com.isaias.fyattravel.web.components.submitInquiry
import com.isaias.fyattravel.web.data.WebProduct
import com.isaias.fyattravel.web.layout.ResponsiveStyles
import com.isaias.fyattravel.web.i18n.WebStringKey
import com.isaias.fyattravel.web.i18n.WebStrings
import com.isaias.fyattravel.web.i18n.WebLocaleStore
import com.isaias.fyattravel.web.theme.ThemeColors
import com.isaias.fyattravel.web.canonicalCategoryKey

private const val FUNCTIONS_BASE = "https://us-central1-fyattravelnew.cloudfunctions.net"

/**
 * Flights page: collects trip details and emails info@fyattraveltour.com
 * instead of performing a live flight search.
 */
@Composable
fun FlightsPage(onNavigate: (String) -> Unit, openEnquiry: Boolean = false) {
    var sending by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var successMessage by remember { mutableStateOf<String?>(null) }

    // Flight offers from products
    var flightOffers by remember { mutableStateOf(emptyList<WebProduct>()) }
    var offersLoading by remember { mutableStateOf(true) }

    // Fetch flight offers — discover exact category name from listProductCategories first
    LaunchedEffect(Unit) {
        fun parseProducts(text: String): List<WebProduct> {
            return try {
                val obj = kotlin.js.JSON.parse<dynamic>(text)
                val ok = (obj.success as Boolean?) == true
                if (!ok) return emptyList()
                val arr = (obj.products as Array<dynamic>?) ?: emptyArray()
                arr.map { d ->
                    WebProduct(
                        id = d.id as String? ?: "",
                        title = d.title as String?,
                        description = d.description as String?,
                        thumbnail = d.thumbnail as String?,
                        category = d.category as String?,
                        createdAt = (d.createdAt as Double?)?.toLong(),
                        priceUsd = (d.priceUsd as Double?)?.toInt(),
                        priceUgx = (d.priceUgx as Double?)?.toInt(),
                    )
                }.filter { it.id.isNotBlank() }
            } catch (t: dynamic) {
                console.error("Failed to parse products", t)
                emptyList()
            }
        }

        fun fetchByCategory(catName: String, limit: Int, onDone: (List<WebProduct>) -> Unit) {
            val bodyJson = """{"category":"$catName","limit":$limit}"""
            window.fetch(
                "$FUNCTIONS_BASE/listProductsByCategory",
                RequestInit(
                    method = "POST",
                    headers = Headers().also { h -> h.append("Content-Type", "application/json") },
                    body = bodyJson
                )
            ).then { resp ->
                resp.text().then { text -> onDone(parseProducts(text)) }
            }.catch { _ -> onDone(emptyList()) }
        }

        // Step 1: Ask the backend what exact category names exist
        window.fetch("$FUNCTIONS_BASE/listProductCategories")
            .then { resp -> resp.text().then { text ->
                try {
                    val obj = kotlin.js.JSON.parse<dynamic>(text)
                    val ok = (obj.success as Boolean?) == true
                    if (ok) {
                        val arr = (obj.categories as Array<dynamic>?) ?: emptyArray()
                        val catNames = arr.map { d -> d.name as String? ?: "" }.filter { it.isNotBlank() }
                        val exactFlightName = catNames.firstOrNull { canonicalCategoryKey(it) == "flight" }

                        if (exactFlightName != null) {
                            // Step 2: Fetch products using the EXACT name the backend recognizes
                            fetchByCategory(exactFlightName, 20) { items ->
                                if (items.isNotEmpty()) flightOffers = items
                                offersLoading = false
                            }
                        } else {
                            // No flight category found — fallback to hardcoded names
                            fetchByCategory("flightOffers", 20) { items ->
                                if (items.isNotEmpty()) {
                                    flightOffers = items
                                    offersLoading = false
                                } else {
                                    fetchByCategory("flight", 20) { items2 ->
                                        if (items2.isNotEmpty()) flightOffers = items2
                                        offersLoading = false
                                    }
                                }
                            }
                        }
                    } else {
                        offersLoading = false
                    }
                } catch (t: dynamic) {
                    console.error("Failed to discover flight category", t)
                    offersLoading = false
                }
            }}
            .catch { _ ->
                // Network error on listProductCategories — fallback to hardcoded names
                fetchByCategory("flightOffers", 20) { items ->
                    if (items.isNotEmpty()) {
                        flightOffers = items
                        offersLoading = false
                    } else {
                        fetchByCategory("flight", 20) { items2 ->
                            if (items2.isNotEmpty()) flightOffers = items2
                            offersLoading = false
                        }
                    }
                }
            }
    }

    fun submitFlightInquiry(params: FlightSearchParams) {
        sending = true
        error = null
        successMessage = null

        submitInquiry(
            type = "flight",
            email = params.email,
            fields = linkedMapOf(
                "Trip type" to params.tripType.name,
                "From" to params.origin.trim(),
                "To" to params.destination.trim(),
                "Departure" to params.departureDate.trim(),
                "Return" to (params.returnDate?.trim().orEmpty()),
                "Adults" to params.adults.toString(),
                "Cabin class" to params.cabinClass.name,
            )
        ) { ok, message ->
            sending = false
            if (ok) {
                successMessage = WebStrings.get(WebLocaleStore.locale.value, WebStringKey.ENQUIRY_SENT)
            } else {
                error = message ?: "Network error. Please check your connection."
            }
        }
    }

    DynamicHeroSection(
        onSearchFlights = { onNavigate("/flights") },
        onSearchHotels = { onNavigate("/stays") }
    )
    ServiceTabs(activeRoute = "/flights", onNavigate = onNavigate)

    EnhancedFlightSearchCard { params ->
        submitFlightInquiry(params)
    }

    // Loading spinner
    if (sending) {
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                gap(1.em)
                padding(2.em)
                color(ThemeColors.textSecondary)
            }
        }) {
            Div({
                style {
                    width(36.px)
                    height(36.px)
                    borderRadius(50.percent)
                    property("border", "3px solid ${ThemeColors.border}")
                    property("border-top-color", "${ThemeColors.primary}")
                    property("animation", "spin 0.8s linear infinite")
                }
            }) {}
        }
    }

    // Error
    error?.let { msg ->
        Div({
            style {
                backgroundColor(ThemeColors.errorBg)
                color(ThemeColors.error)
                padding(1.em)
                borderRadius(0.75.em)
                marginBottom(1.em)
                fontWeight("600")
            }
        }) { Text(msg) }
    }

    // Success
    successMessage?.let { msg ->
        Div({
            style {
                backgroundColor(ThemeColors.successBg)
                color(ThemeColors.success)
                padding(1.em)
                borderRadius(0.75.em)
                marginBottom(1.em)
                fontWeight("600")
            }
        }) { Text(msg) }
    }

    // Booking enquiry form - always visible below search
    // If opened via Visa Processing link, expand automatically
    BookingEnquiryForm(initialExpanded = openEnquiry)

    // Flight Offers Section (shown directly below Booking Enquiry)
    Div({
        style {
            marginTop(1.25.em)
            marginBottom(2.em)
        }
    }) {
        H2({
            style {
                fontSize(1.4.em)
                fontWeight("800")
                color(ThemeColors.textPrimary)
                marginBottom(1.em)
            }
        }) { Text(WebStrings.t(WebStringKey.FLIGHT_OFFERS)) }

        if (offersLoading) {
            Div({
                style {
                    display(DisplayStyle.Grid)
                    gridTemplateColumns("repeat(auto-fill, minmax(280px, 1fr))")
                    gap(1.em)
                    property("@media (max-width: 900px)", "{ grid-template-columns: repeat(2, minmax(0, 1fr)); }")
                    property("@media (max-width: 640px)", "{ grid-template-columns: 1fr; }")
                }
            }) {
                repeat(4) {
                    Div({
                        style {
                            backgroundColor(ThemeColors.skeleton)
                            height(200.px)
                            borderRadius(1.em)
                            property("animation", "pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite")
                        }
                    }) {}
                }
            }
        } else if (flightOffers.isNotEmpty()) {
            Div({
                style {
                    display(DisplayStyle.Grid)
                    gridTemplateColumns("repeat(auto-fill, minmax(280px, 1fr))")
                    gap(1.em)
                    property("@media (max-width: 900px)", "{ grid-template-columns: repeat(2, minmax(0, 1fr)); }")
                    property("@media (max-width: 640px)", "{ grid-template-columns: 1fr; }")
                }
            }) {
                flightOffers.forEach { offer ->
                    val priceParts = listOfNotNull(
                        offer.priceUsd?.let { "USD $it" },
                        offer.priceUgx?.let { "UGX $it" }
                    )

                    Div({
                        style {
                            backgroundColor(ThemeColors.surface)
                            borderRadius(1.em)
                            overflow("hidden")
                            property("border", "1px solid ${ThemeColors.border}")
                            property("box-shadow", "0 2px 10px rgba(0,0,0,0.04)")
                            property("transition", "all 0.2s ease")
                            cursor("pointer")
                        }
                        onClick { onNavigate("/checkout?productId=${offer.id}") }
                    }) {
                        offer.thumbnail?.let { url ->
                            if (url.isNotBlank()) {
                                Img(src = url, attrs = {
                                    style {
                                        width(100.percent)
                                        height(160.px)
                                        property("object-fit", "cover")
                                    }
                                })
                            }
                        }

                        Div({
                            style {
                                padding(1.25.em)
                            }
                        }) {
                            H3({
                                style {
                                    fontSize(1.1.em)
                                    fontWeight("700")
                                    color(ThemeColors.textPrimary)
                                    marginBottom(0.5.em)
                                }
                            }) { Text(offer.title ?: WebStrings.t(WebStringKey.FLIGHT_OFFER_LABEL)) }

                            offer.description?.let { desc ->
                                if (desc.isNotBlank()) {
                                    P({
                                        style {
                                            color(ThemeColors.textSecondary)
                                            fontSize(0.9.em)
                                            lineHeight("1.5")
                                            marginBottom(1.em)
                                            property("display", "-webkit-box")
                                            property("-webkit-line-clamp", "2")
                                            property("-webkit-box-orient", "vertical")
                                            property("overflow", "hidden")
                                        }
                                    }) { Text(desc) }
                                }
                            }

                            if (priceParts.isNotEmpty()) {
                                Div({
                                    style {
                                        backgroundColor(ThemeColors.successBg)
                                        padding(0.75.em)
                                        borderRadius(0.5.em)
                                        marginBottom(1.em)
                                        display(DisplayStyle.Flex)
                                        justifyContent(JustifyContent.SpaceBetween)
                                        alignItems(AlignItems.Center)
                                    }
                                }) {
                                    Span({
                                        style {
                                            color(ThemeColors.success)
                                            fontWeight("600")
                                            fontSize(0.85.em)
                                        }
                                    }) { Text(WebStrings.t(WebStringKey.FLIGHT_PRICE)) }
                                    Span({
                                        style {
                                            color(ThemeColors.success)
                                            fontWeight("800")
                                            fontSize(1.1.em)
                                        }
                                    }) { Text(priceParts.joinToString(" • ")) }
                                }
                            }

                            Button({
                                style {
                                    width(100.percent)
                                    backgroundColor(ThemeColors.primary)
                                    color(ThemeColors.textOnGold)
                                    padding(0.75.em)
                                    borderRadius(0.75.em)
                                    border(0.px)
                                    cursor("pointer")
                                    fontWeight("700")
                                    fontSize(0.95.em)
                                    property("box-shadow", "0 2px 8px rgba(37,99,235,0.25)")
                                }
                                onClick {
                                    it.stopPropagation()
                                    onNavigate("/checkout?productId=${offer.id}")
                                }
                            }) {
                                Text(WebStrings.t(WebStringKey.FLIGHT_BOOK_NOW_FROM))
                            }
                        }
                    }
                }
            }
        } else {
            Div({
                style {
                    textAlign("center")
                    padding(1.25.em)
                    color(ThemeColors.textSecondary)
                    backgroundColor(ThemeColors.surface)
                    borderRadius(1.em)
                    property("border", "1px solid ${ThemeColors.border}")
                }
            }) {
                Text("No flight offers available right now.")
            }
        }
    }



}
